import { Mic2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TtsProviderStyleSelect } from '@/components/tts/TtsProviderStyleSelect';
import {
  NARRATION_AUDIO_LANGUAGE_OPTIONS,
  NARRATION_TTS_PROVIDER_OPTIONS,
  narrationSelectedVoiceLabel,
} from '@/lib/narrationAudioSettings';
import {
  defaultVoiceStyleForProvider,
  normalizeVoiceStyleForProvider,
  providerStyleFieldLabel,
  shouldShowProviderStyleSelect,
} from '@/lib/ttsProviderStyles';

/**
 * Compact audio provider + voice controls for the narration scene grid header.
 * @param {{ layout?: 'inline' | 'block', murfVoices?: object[] }} props
 */
export function NarrationAudioSettingsBar({
  settings,
  onChange,
  onChooseVoice,
  disabled = false,
  layout = 'inline',
  murfVoices = [],
}) {
  const voiceLabel = narrationSelectedVoiceLabel(settings);
  const isBlock = layout === 'block';

  const patch = (partial) => onChange?.({ ...settings, ...partial });

  const handleProviderChange = (provider) => {
    const murfOpts = {
      murfVoices,
      murfVoiceId: settings?.narrator_murf_voice_id,
      murfLocale: settings?.narrator_murf_locale,
      language: settings?.language,
    };
    const providerChanged = provider !== settings?.tts_provider;
    const nextStyle = shouldShowProviderStyleSelect(provider)
      ? (providerChanged
        ? defaultVoiceStyleForProvider(provider)
        : normalizeVoiceStyleForProvider(provider, settings?.voice_style, murfOpts))
      : settings?.voice_style;
    onChange?.({
      ...settings,
      tts_provider: provider,
      voice_style: nextStyle,
    });
  };

  const shellClass = isBlock
    ? 'grid grid-cols-1 sm:grid-cols-2 gap-2'
    : 'flex flex-wrap items-center gap-2 rounded-lg border px-2 py-1.5';

  const shellStyle = isBlock
    ? undefined
    : { borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' };

  const fieldShell = (label, testId, children) => (
    isBlock ? (
      <div className="flex flex-col gap-1" data-testid={testId}>
        <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>{label}</span>
        {children}
      </div>
    ) : children
  );

  const triggerClass = isBlock
    ? 'h-9 w-full rounded-lg text-xs border'
    : 'h-8 w-[min(100%,7.5rem)] rounded-lg text-xs border-0 shadow-none';

  const triggerStyle = isBlock
    ? { borderColor: 'var(--glass-border)', color: 'var(--text-primary)', background: 'var(--bg-tertiary)' }
    : { color: 'var(--text-primary)', background: 'transparent' };

  const providerStyleTriggerClass = isBlock
    ? triggerClass
    : `${triggerClass} sm:max-w-[8.5rem]`;

  return (
    <div
      className={shellClass}
      style={shellStyle}
      data-testid="narration-audio-settings-bar"
    >
      {isBlock ? (
        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {fieldShell('Audio provider', 'narration-tts-provider-field', (
            <Select
              value={settings.tts_provider || 'auto'}
              onValueChange={handleProviderChange}
              disabled={disabled}
            >
              <SelectTrigger
                className={triggerClass}
                style={triggerStyle}
                data-testid="narration-tts-provider-select"
              >
                <SelectValue placeholder="TTS provider" />
              </SelectTrigger>
              <SelectContent>
                {NARRATION_TTS_PROVIDER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
          {shouldShowProviderStyleSelect(settings.tts_provider) ? fieldShell(
            providerStyleFieldLabel(settings.tts_provider),
            'narration-voice-style-field',
            (
              <TtsProviderStyleSelect
                provider={settings.tts_provider}
                value={settings.voice_style}
                onChange={(v) => patch({ voice_style: v })}
                disabled={disabled}
                murfVoices={murfVoices}
                murfVoiceId={settings.narrator_murf_voice_id}
                murfLocale={settings.narrator_murf_locale}
                language={settings.language}
                triggerClassName={triggerClass}
                triggerStyle={triggerStyle}
                testId="narration-voice-style-select"
              />
            ),
          ) : null}
        </div>
      ) : (
        <>
          {fieldShell('Audio provider', 'narration-tts-provider-field', (
            <Select
              value={settings.tts_provider || 'auto'}
              onValueChange={handleProviderChange}
              disabled={disabled}
            >
              <SelectTrigger
                className={triggerClass}
                style={triggerStyle}
                data-testid="narration-tts-provider-select"
              >
                <SelectValue placeholder="TTS provider" />
              </SelectTrigger>
              <SelectContent>
                {NARRATION_TTS_PROVIDER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
          <TtsProviderStyleSelect
            provider={settings.tts_provider}
            value={settings.voice_style}
            onChange={(v) => patch({ voice_style: v })}
            disabled={disabled}
            murfVoices={murfVoices}
            murfVoiceId={settings.narrator_murf_voice_id}
            murfLocale={settings.narrator_murf_locale}
            language={settings.language}
            triggerClassName={providerStyleTriggerClass}
            triggerStyle={triggerStyle}
            testId="narration-voice-style-select"
          />
        </>
      )}

      {fieldShell('Language', 'narration-audio-language-field', (
        <Select
          value={settings.language || 'en'}
          onValueChange={(v) => patch({ language: v })}
          disabled={disabled}
        >
          <SelectTrigger
            className={isBlock ? triggerClass : `${triggerClass} sm:max-w-[6.5rem]`}
            style={triggerStyle}
            data-testid="narration-audio-language-select"
          >
            <SelectValue placeholder="Language" />
          </SelectTrigger>
          <SelectContent>
            {NARRATION_AUDIO_LANGUAGE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ))}

      {settings.tts_provider && settings.tts_provider !== 'auto' ? (
        isBlock ? (
          fieldShell('Voice', 'narration-voice-field', (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 w-full rounded-lg text-xs gap-1 justify-start"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
              disabled={disabled}
              onClick={onChooseVoice}
              data-testid="narration-choose-voice-btn"
            >
              <Mic2 className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{voiceLabel}</span>
            </Button>
          ))
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 rounded-lg text-xs border-0 shadow-none gap-1"
            style={{ color: 'var(--text-primary)' }}
            disabled={disabled}
            onClick={onChooseVoice}
            data-testid="narration-choose-voice-btn"
          >
            <Mic2 className="w-3.5 h-3.5" />
            Voice
          </Button>
        )
      ) : null}

      {!isBlock ? (
        <span
          className="text-[10px] max-w-[10rem] truncate hidden sm:inline"
          style={{ color: 'var(--text-tertiary)' }}
          title={voiceLabel}
        >
          {voiceLabel}
        </span>
      ) : null}
    </div>
  );
}
